import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentId } from '@hotjar/webapp/features/Recordings/types';

import type { FormMode } from '../FormModeOption';
import { useSegmentReset } from '../use-segment-reset';

export function useSegmentName(
  segmentId: SegmentId | null,
  initialName: string,
  formMode: FormMode,
): [string, (newName: string) => void] {
  const [name, setName] = useState<string>(initialName);
  const { t } = useTranslation();

  const previousMode = useRef<FormMode | null>(null);
  useEffect(() => {
    const previousModeValue = previousMode.current;
    previousMode.current = formMode;

    if (previousModeValue !== null && formMode === previousModeValue) {
      return;
    }

    if (formMode === 'update' || name !== initialName) {
      return;
    }

    const nameWithSuffix = t('common:recordings.segment-edit-create.name.copy-suffix', {
      segmentName: name,
    });

    setName(nameWithSuffix);
  }, [formMode, initialName, name, setName, t]);

  const resetName = useCallback(() => {
    setName(initialName);
  }, [setName, initialName]);
  useSegmentReset(segmentId, resetName);

  return [name, setName];
}
